import React, { useState, useRef, useEffect } from "react";
import UNSPSCResultsTable from "./components/ui/UNSPSCResultsTable";
import { generateStreamingResponse } from "./utils/streamingHelper";
import { analyzeImage, analyzeImageWithGemini, fileToBase64 } from "./services/imageApi.service";
import DOMPurify from "dompurify";

// Google Analytics gtag.js loader
const GA_MEASUREMENT_ID = "G-RF95CX1PDE";
function loadGAScript() {
  if (!window.gtag) {
    // Inject the gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
    // Setup dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, { 'debug_mode': true });
  }
}

/**
 * Detects the intent of a user's question based on keywords.
 * @param {string} question - The user's question or input.
 * @returns {string} The detected intent label.
 */
function detectIntent(question) {
  const q = question.toLowerCase();
  const intents = [
    // Authentication
    { keywords: ['login', 'log masuk', 'sign in', 'masuk sistem'], label: 'login' },
    { keywords: ['register', 'daftar', 'sign up', 'pendaftaran'], label: 'register' },
    { keywords: [
        'reset password', 'lupa kata laluan', 'forgot password', 'tukar kata laluan', 
        'change password'
      ], label: 'reset_password' },
    { keywords: ['logout', 'log keluar', 'sign out', 'keluar'], label: 'logout' },

    // Procurement & Tender
    { keywords: [
        'tender', 'quotation', 'sebut harga', 'bidaan', 'tawaran'
      ], label: 'tender_quotation' },
    { keywords: [
        'procurement', 'perolehan', 'purchase', 'pembelian', 'order', 'pesanan', 
        'permohonan perolehan'
      ], label: 'procurement_purchase' },

    // Vendor/Supplier
    { keywords: [
        'vendor', 'supplier', 'pembekal', 'syarikat', 'company', 
        'vendor registration', 'pendaftaran pembekal'
      ], label: 'vendor_supplier' },

    // Payment & Billing
    { keywords: [
        'payment', 'bayaran', 'pembayaran', 'yuran', 'fee'
      ], label: 'payment' },
    { keywords: [
        'invoice', 'invois', 'billing', 'bil', 'claim', 'tuntutan', 'resit', 'receipt'
      ], label: 'invoice_billing' },

    // Contract/Agreement
    { keywords: [
        'contract', 'agreement', 'kontrak', 'perjanjian', 'dokumen kontrak'
      ], label: 'contract_agreement' },

    // Document Handling
    { keywords: [
        'upload', 'muat naik', 'submit', 'hantar', 'dokumen', 'document', 
        'lampiran', 'attachment'
      ], label: 'document_upload' },
    { keywords: [
        'download', 'muat turun', 'download document'
      ], label: 'download' },

    // Status/Tracking
    { keywords: [
        'status', 'tracking', 'jejak', 'progress', 'kemajuan', 'update', 
        'kemaskini', 'semakan status', 'status permohonan', 'registration status', 
        'status pendaftaran'
      ], label: 'status_tracking' },

    // Profile/Account
    { keywords: [
        'profile', 'akaun', 'account', 'profil', 'kemaskini profil', 
        'update profile', 'profile update', 'kemaskini akaun', 'update account'
      ], label: 'profile_account' },
    { keywords: [
        'profile verification', 'pengesahan profil', 'verify account'
      ], label: 'profile_verification' },

    // Support/Help/Contact
    { keywords: [
        'support', 'bantuan', 'pertolongan', 'sokongan teknikal', 'pertanyaan', 
        'contact', 'hubungi', 'sokongan', 'hotline', 'nombor telefon', 
        'email', 'emel'
      ], label: 'contact_support' },
    { keywords: [
        'help', 'faq', 'soalan lazim', 'panduan', 'manual', 'tutorial', 
        'user manual', 'panduan pengguna', 'manual pengguna'
      ], label: 'help_faq' },

    // System/Technical
    { keywords: [
        'error', 'masalah', 'issue', 'bug', 'ralat', 'technical', 'teknikal', 
        'server down', 'server error', 'system maintenance', 'penyelenggaraan sistem', 
        'maintenance'
      ], label: 'system_error' },

    // Blacklist/Disqualification
    { keywords: [
        'blacklist', 'senarai hitam', 'disqualified'
      ], label: 'blacklist' },

    // Announcement/Notification
    { keywords: [
        'announcement', 'pengumuman', 'notis', 'reminder', 'peringatan', 
        'notifikasi', 'notification'
      ], label: 'announcement' },

    // Evaluation/Award/Decision
    { keywords: [
        'evaluation', 'penilaian', 'assessment'
      ], label: 'evaluation' },
    { keywords: [
        'award', 'anugerah', 'decision', 'keputusan'
      ], label: 'award_decision' },

    // Report/Complaint/Feedback
    { keywords: [
        'report', 'laporan', 'aduan', 'complaint', 'feedback', 'maklum balas'
      ], label: 'report_complaint' },

    // Renewal
    { keywords: [
        'renewal', 'pembaharuan', 'renew'
      ], label: 'renewal' },

    // Training
    { keywords: [
        'training', 'latihan', 'kursus'
      ], label: 'training' },
  ];

  for (const { keywords, label } of intents) {
    if (keywords.some(keyword => q.includes(keyword))) {
      return label;
    }
  }
  return 'other';
}

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faUpload,
  faMicrophone,
  faBars,
  faFileImport,
  faVolumeUp,
  faWindowMinimize,
  faSquare,
  faCode,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import { TextShimmer } from "./components/ui/text-shimmer";

const Chatbot = () => {
  useEffect(() => {
    loadGAScript();
  }, []);
  const [userInput, setUserInput] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState("");
  const [streamingStartTime, setStreamingStartTime] = useState(null);
  const [streamingDuration, setStreamingDuration] = useState(0);
  const [showWidget, setShowWidget] = useState(false);
  const [modernMinimized, setModernMinimized] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [detectedLabels, setDetectedLabels] = useState([]);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [visionModel, setVisionModel] = useState('vision'); // 'vision' or 'gemini'
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (chatHistory.length === 0) {
      setChatHistory([{ role: "bot", content: "Hai, saya SoPhia - Catalog AI Assistant v1.0. Saya akan membantu anda mencari kod UNSPSC untuk produk atau perkhidmatan.<br><br>Anda boleh bercakap dengan saya dalam Bahasa Melayu dan juga Bahasa Inggeris" }]);
    }
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);

  // Auto-scroll during streaming
  useEffect(() => {
    if (streamingResponse && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [streamingResponse]);

  const handleInputChange = (e) => setUserInput(e.target.value);

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload a valid image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size should be less than 10MB');
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = async (e) => {
      const imagePreviewUrl = e.target.result;
      setImagePreview(imagePreviewUrl);

      // Add image upload message to chat history
      setChatHistory(prev => [...prev, { 
        role: "user", 
        content: "IMAGE_UPLOAD",
        imagePreview: imagePreviewUrl,
        isAnalyzing: true
      }]);

      // Analyze image with selected model
      setIsAnalyzingImage(true);
      
      try {
        const base64Image = await fileToBase64(file);
        let result;
        
        if (visionModel === 'gemini') {
          result = await analyzeImageWithGemini(base64Image, 10);
        } else {
          result = await analyzeImage(base64Image, 10, 0.5);
        }
        
        const labels = result.labels || [];
        setDetectedLabels(labels);
        
        // Update the last message with analysis results
        setChatHistory(prev => {
          const updated = [...prev];
          const lastIndex = updated.length - 1;
          if (updated[lastIndex]?.content === "IMAGE_UPLOAD") {
            updated[lastIndex] = {
              ...updated[lastIndex],
              isAnalyzing: false,
              labels: labels,
              suggestedQuery: result.suggestedQuery
            };
          }
          return updated;
        });
        
        // Auto-populate input with suggested query
        if (result.suggestedQuery) {
          setUserInput(result.suggestedQuery);
        }
        
        console.log('Image analysis result:', result);
      } catch (error) {
        console.error('Image analysis failed:', error);
        alert('Failed to analyze image. Please try again.');
        // Remove the failed upload message
        setChatHistory(prev => prev.filter(msg => msg.content !== "IMAGE_UPLOAD"));
      } finally {
        setIsAnalyzingImage(false);
      }
    };
    reader.readAsDataURL(file);
  };

  // Clear image
  const handleClearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setDetectedLabels([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle label click (search immediately)
  const handleLabelClick = async (labelDescription) => {
    if (!labelDescription.trim()) return;

    const startTime = performance.now(); // Start timing

    // Intent detection and Google Analytics event
    let intent = 'unspsc_query';
    if (window.gtag) {
      window.gtag('event', 'chat_question', {
        'event_category': 'chatbot',
        'event_label': intent,
        'value': 1
      });
      console.log('GA event sent: chat_question', { intent });
    } else {
      console.warn('window.gtag is not defined. GA event not sent.');
    }

    const historyForBackend = chatHistory.map(msg => ({
      role: msg.role === "bot" ? "assistant" : "user",
      content: msg.role === "bot" && msg.content === "UNSPSC_RESULTS" ? "UNSPSC results were displayed" : msg.content,
    }));

    setChatHistory(prev => [...prev, { role: "user", content: labelDescription }]);
    setUserInput("");
    setIsLoading(true);
    setStreamingResponse("");

    try {
      let firstChunk = true;
      let streamStart = null;
      const data = await generateStreamingResponse(
        {
          query: labelDescription,
          history: historyForBackend,
          unspscContext: true,
          userId: sessionStorage.getItem('userId') || 'anonymous',
          sessionId: sessionStorage.getItem('sessionId') || Date.now().toString(),
        },
        (chunk, fullText) => {
          // Turn off loading indicator on first chunk
          if (firstChunk) {
            setIsLoading(false);
            streamStart = Date.now();
            setStreamingStartTime(streamStart);
            firstChunk = false;
          }
          // Update streaming response in real-time
          if (streamStart) {
            setStreamingDuration(((Date.now() - streamStart) / 1000).toFixed(2));
          }
          setStreamingResponse(formatBotResponse(fullText));
        }
      );
      const endTime = performance.now(); // End timing
      const processingTime = ((endTime - startTime) / 1000).toFixed(2); // Convert to seconds

      let modelResponse =
        data &&
        Array.isArray(data.candidates) &&
        data.candidates.length > 0 &&
        data.candidates[0].content &&
        data.candidates[0].content.parts &&
        Array.isArray(data.candidates[0].content.parts) &&
        data.candidates[0].content.parts.length > 0 &&
        data.candidates[0].content.parts[0].text
          ? data.candidates[0].content.parts[0].text
          : "No response available.";
          
      // Check if this is a UNSPSC response
      const isUNSPSCResponse = modelResponse === "UNSPSC_RESULTS" && data.unspscResults;
      
      if (!isUNSPSCResponse) {
        modelResponse = formatBotResponse(modelResponse);
      }

      setTimeout(() => {
        if (isUNSPSCResponse) {
          setChatHistory(prev => [
            ...prev,
            { 
              role: "bot", 
              content: "UNSPSC_RESULTS",
              unspscResults: data.unspscResults,
              processingTime 
            }
          ]);
        } else {
          setChatHistory(prev => [...prev, { role: "bot", content: modelResponse, processingTime }]);
        }
        setStreamingResponse("");
        setStreamingStartTime(null);
        setStreamingDuration(0);
        setIsLoading(false);
      }, 1000);

    } catch (error) {
      console.error("Fetch error:", error);
      setChatHistory(prev => [...prev, { role: "bot", content: `Error: ${error.message}` }]);
      setIsLoading(false);
    }
  };

  const formatBotResponse = (response) => {
    // Try to parse as JSON and extract 'response' property if present
    let text = response;
    try {
      const parsed = JSON.parse(response);
      if (parsed && typeof parsed === 'object') {
        if (parsed.response) {
          text = parsed.response;
        } else if (parsed.text) {
          text = parsed.text;
        }
      }
    } catch (e) {
      // Not JSON, proceed as normal
    }
    // Remove markdown bold (**) if present
    text = text.replace(/\*\*/g, "");
    const lines = text.split("\n");
    let formatted = "";
    let inList = false;
    let listType = "";
    lines.forEach((line) => {
      line = line.trim();
      if (line.startsWith("**")) {
        if (inList) formatted += `</${listType}>`;
        formatted += `<strong>${line.replace(/\*\*/g, "")}</strong>`;
        inList = false;
      } else if (line.startsWith("*")) {
        if (!inList || listType !== "ul") {
          if (inList) formatted += `</${listType}>`;
          formatted += "<ul>";
          listType = "ul";
          inList = true;
        }
        formatted += `<li>${line.replace(/^\*\s*/, "")}</li>`;
      } else if (line === "") {
        if (inList) formatted += `</${listType}>`;
        inList = false;
      } else {
        if (inList) formatted += `</${listType}>`;
        formatted += `<p>${line}</p>`;
        inList = false;
      }
    });
    if (inList) formatted += `</${listType}>`;
    return formatted;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;

    const startTime = performance.now(); // Start timing

    // Intent detection and Google Analytics event
    let intent = 'unspsc_query';
    if (window.gtag) {
      window.gtag('event', 'chat_question', {
        'event_category': 'chatbot',
        'event_label': intent,
        'value': 1
      });
      console.log('GA event sent: chat_question', { intent });
    } else {
      console.warn('window.gtag is not defined. GA event not sent.');
    }

    const historyForBackend = chatHistory.map(msg => ({
      role: msg.role === "bot" ? "assistant" : "user",
      content: msg.role === "bot" && msg.content === "UNSPSC_RESULTS" ? "UNSPSC results were displayed" : msg.content,
    }));

    setChatHistory(prev => [...prev, { role: "user", content: userInput }]);
    setUserInput("");
    setIsLoading(true);
    setStreamingResponse("");

    try {
      let firstChunk = true;
      let streamStart = null;
      const data = await generateStreamingResponse(
        {
          query: userInput,
          history: historyForBackend,
          unspscContext: true,
          userId: sessionStorage.getItem('userId') || 'anonymous',
          sessionId: sessionStorage.getItem('sessionId') || Date.now().toString(),
        },
        (chunk, fullText) => {
          // Turn off loading indicator on first chunk
          if (firstChunk) {
            setIsLoading(false);
            streamStart = Date.now();
            setStreamingStartTime(streamStart);
            firstChunk = false;
          }
          // Update streaming response in real-time
          if (streamStart) {
            setStreamingDuration(((Date.now() - streamStart) / 1000).toFixed(2));
          }
          setStreamingResponse(formatBotResponse(fullText));
        }
      );
      const endTime = performance.now(); // End timing
      const processingTime = ((endTime - startTime) / 1000).toFixed(2); // Convert to seconds

      let modelResponse =
        data &&
        Array.isArray(data.candidates) &&
        data.candidates.length > 0 &&
        data.candidates[0].content &&
        data.candidates[0].content.parts &&
        Array.isArray(data.candidates[0].content.parts) &&
        data.candidates[0].content.parts.length > 0 &&
        data.candidates[0].content.parts[0].text
          ? data.candidates[0].content.parts[0].text
          : "No response available.";
          
      // Check if this is a UNSPSC response
      const isUNSPSCResponse = modelResponse === "UNSPSC_RESULTS" && data.unspscResults;
      
      if (!isUNSPSCResponse) {
        modelResponse = formatBotResponse(modelResponse);
      }

      setTimeout(() => {
        if (isUNSPSCResponse) {
          setChatHistory(prev => [
            ...prev,
            { 
              role: "bot", 
              content: "UNSPSC_RESULTS",
              unspscResults: data.unspscResults,
              processingTime 
            }
          ]);
        } else {
          setChatHistory(prev => [...prev, { role: "bot", content: modelResponse, processingTime }]);
        }
        setStreamingResponse("");
        setStreamingStartTime(null);
        setStreamingDuration(0);
        setIsLoading(false);
      }, 1000);

      } catch (error) {
      console.error("Fetch error:", error);
      setChatHistory(prev => [...prev, { role: "bot", content: `Error: ${error.message}` }]);
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* AI Assistant Bar (always visible at bottom) */}
      {/* Overlay for closing on outside click */}
      {showWidget && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1000,
            background: 'transparent',
          }}
          onClick={() => setShowWidget(false)}
        />
      )}
      <div
        className={`ai-assistant-bar${showWidget ? ' open' : ''}`}
        onClick={() => !showWidget && setShowWidget(true)}
        style={{ zIndex: 1001 }}
      >
        {!modernMinimized && (
          <div className="ai-assistant-bar-content">
            <span className="ai-assistant-label">SoPhia – Catalog AI Assistant v1.0</span>
          </div>
        )}
        {/* Chat Panel (slider) */}
        <div
          className={`ai-assistant-panel${showWidget ? ' open' : ''}${modernMinimized ? ' modern-minimized' : ''}`}
          style={
            modernMinimized
              ? {
                  zIndex: 1002,
                  position: 'fixed',
                  right: 20,
                  bottom: 20,
                  width: '30vw',
                  height: '60vh',
                  minWidth: 320,
                  minHeight: 320,
                  maxWidth: 500,
                  maxHeight: 700,
                  borderRadius: 16,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
                  background: '#fff',
                  display: showWidget ? 'flex' : 'none',
                  flexDirection: 'column',
                }
              : { zIndex: 1002 }
          }
          onClick={e => e.stopPropagation()}
        >
          {!modernMinimized && showWidget ? (
            // Main chat interface with static sidebar
            <div style={{ display: 'flex', height: '100%' }}>
              {/* Chat content area */}
              <div style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                <div className="chat-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between', paddingRight: 8}}>
                  <div style={{display:'flex', alignItems:'center', flex:1}}>
                    <img src="https://epol.eperolehan.gov.my/ep/www/images/img/circle.png" alt="Logo" className="ai-assistant-logo" style={{width:40, height:40, borderRadius: '50%', marginRight: 12}} />
                    <h1 style={{margin:0, fontSize: undefined}}><b>SoPhia</b> - Catalog AI Assistant v1.0</h1>
                  </div>
                  <div style={{display:'flex', alignItems:'center', gap: 8}}>
                    <select 
                      value={visionModel} 
                      onChange={(e) => setVisionModel(e.target.value)}
                      style={{
                        padding: '0.4rem 0.6rem',
                        fontSize: '0.8rem',
                        borderRadius: '6px',
                        border: '1px solid #ddd',
                        background: '#fff',
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                      title="Select image analysis model"
                    >
                      <option value="vision">Vision API</option>
                      <option value="gemini">Gemini Pro</option>
                    </select>
                    <button
                      onClick={() => {
                        setShowWidget(false);
                        setTimeout(() => setModernMinimized(false), 300); // Match CSS transition duration
                      }}
                      className="minimize-btn"
                      aria-label="Minimize chatbot"
                    >
                      <FontAwesomeIcon icon={faWindowMinimize} />
                    </button>
                    <button
                      onClick={() => setModernMinimized(m => !m)}
                      className="modern-minimize-btn"
                      aria-label="Modern Minimize"
                      title="Switch to modern minimized view"
                    >
                      <FontAwesomeIcon icon={faSquare} />
                    </button>
                  </div>
                </div>
                <div className="chat-history" ref={chatContainerRef}>
                  {chatHistory.map((msg, i) => (
                    <div key={i} className={`message ${msg.role}`}>
                        {msg.content === "IMAGE_UPLOAD" ? (
                          // Image upload message with labels
                          <div className="message-content">
                            <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                <img 
                                  src={msg.imagePreview} 
                                  alt="Uploaded" 
                                  style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #ddd' }} 
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#333', marginBottom: '0.5rem' }}>
                                    {msg.isAnalyzing ? '🔍 Analyzing image...' : '✔️ Image uploaded & analyzed'}
                                  </div>
                                  {msg.labels && msg.labels.length > 0 && (
                                    <>
                                      <div style={{ fontSize: '0.8rem', color: '#323232', marginBottom: '0.35rem' }}>
                                        Select one of the recommended labels:
                                      </div>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                                        {msg.labels.slice(0, 5).map((label, idx) => (
                                          <span 
                                            key={idx}
                                            onClick={() => handleLabelClick(label.description)}
                                            style={{ 
                                              padding: '0.25rem 0.5rem', 
                                              background: '#e3f2fd', 
                                              color: '#1976d2', 
                                              borderRadius: '12px', 
                                              fontSize: '0.75rem',
                                              cursor: 'pointer',
                                              border: '1px solid #90caf9',
                                              transition: 'all 0.2s',
                                              fontWeight: 'bold'
                                            }}
                                            onMouseEnter={(e) => {
                                              e.target.style.background = '#1976d2';
                                              e.target.style.color = '#fff';
                                            }}
                                            onMouseLeave={(e) => {
                                              e.target.style.background = '#e3f2fd';
                                              e.target.style.color = '#1976d2';
                                            }}
                                            title={`Confidence: ${label.confidence}% - Click to search`}
                                          >
                                            {label.description} ({label.confidence}%)
                                          </span>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : msg.content === "UNSPSC_RESULTS" && msg.unspscResults ? (
                          <>
                            <div className="message-content">
                              <UNSPSCResultsTable results={msg.unspscResults} />
                            </div>
                            {msg.processingTime && (
                              <div style={{ fontSize: '0.75rem', color: '#444444ff', marginTop: '0.5rem' }}>
                                ⏱️ Processing time: {msg.processingTime}s
                              </div>
                            )}
                          </>
                        ) : msg.content && (msg.content.trim().startsWith('{') || msg.content.trim().startsWith('[')) ? (
                          <>
                            <pre className="message-content" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f5f5f5', padding: 12, borderRadius: 6, fontSize: 14, fontFamily: 'monospace'}}>{msg.content}</pre>
                            {msg.processingTime && (
                              <div style={{ fontSize: '0.75rem', color: '#444444ff', marginTop: '0.5rem' }}>
                                ⏱️ Processing time: {msg.processingTime}s
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {msg.role === "bot" ? (
                              <div className="message-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content) }} />
                            ) : (
                              <div className="message-content">{msg.content}</div>
                            )}
                            {msg.processingTime && (
                              <div style={{ fontSize: '0.75rem', color: '#444444ff', marginTop: '0.5rem' }}>
                                ⏱️ Processing time: {msg.processingTime}s
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )
                  )}
                  {isLoading && !streamingResponse && (
                    <div className="message bot">
                      <div className="message-content neon-glow">
                        <TextShimmer className="text-base font-mono" duration={2}>
                          SoPhia is thinking. Give it sometime to process....
                        </TextShimmer>
                      </div>
                    </div>
                  )}
                  {streamingResponse && (
                    <div className="message bot">
                      <div className="message-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(streamingResponse) }} />
                      <div style={{ fontSize: '0.75rem', color: '#444444ff', marginTop: '0.5rem', fontStyle: 'italic' }}>
                        ⚡ Streaming... {streamingDuration}s
                      </div>
                    </div>
                  )}
                </div>
                <form onSubmit={handleSubmit} className="chat-form" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isAnalyzingImage}
                    className="upload-btn"
                    style={{ 
                      padding: '0.6rem', 
                      background: '#4a8534', 
                      color: '#fff', 
                      border: 'none',   
                      borderRadius: '8px', 
                      cursor: isAnalyzingImage ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s'
                    }}
                    title="Upload image to search"
                  >
                    <FontAwesomeIcon icon={faUpload} />
                  </button>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <input
                      type="text"
                      value={userInput}
                      onChange={handleInputChange}
                      placeholder="Enter product or service name to get UNSPSC code..."
                      required
                      style={{ width: '100%', paddingRight: '2.5rem' }}
                    />
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="chat-send-btn"
                      style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            // Minimized chat interface
            <>
              <div className="chat-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between', paddingRight: 8}}>
                <div style={{display:'flex', alignItems:'center', flex:1}}>
                  <img src="https://epol.eperolehan.gov.my/ep/www/images/img/circle.png" alt="Logo" className="ai-assistant-logo" style={{width:40, height:40, borderRadius: '50%', marginRight: 12}} />
                  <h1 style={{margin:0, fontSize: modernMinimized ? '0.9rem' : undefined}}><b>SoPhia</b> - Catalog AI Assistant</h1>
                </div>
                <div style={{display:'flex', alignItems:'center', gap: 8}}>
                  <select 
                    value={visionModel} 
                    onChange={(e) => setVisionModel(e.target.value)}
                    style={{
                      padding: '0.4rem 0.6rem',
                      fontSize: '0.8rem',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      background: '#fff',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                    title="Select image analysis model"
                  >
                    <option value="vision">Vision API</option>
                    <option value="gemini">Gemini Pro</option>
                  </select>
                  <button
                    onClick={() => {
                      setShowWidget(false);
                      setTimeout(() => setModernMinimized(false), 300); // Match CSS transition duration
                    }}
                    className="minimize-btn"
                    aria-label="Minimize chatbot"
                  >
                    <FontAwesomeIcon icon={faWindowMinimize} />
                  </button>
                  <button
                    onClick={() => setModernMinimized(m => !m)}
                    className="modern-minimize-btn"
                    aria-label="Modern Minimize"
                    title="Switch to modern minimized view"
                  >
                    <FontAwesomeIcon icon={faSquare} />
                  </button>
                </div>
              </div>
              <div className="chat-history" ref={chatContainerRef}>
                {chatHistory.map((msg, i) => (
                  <div key={i} className={`message ${msg.role}`}>
                      {msg.content === "IMAGE_UPLOAD" ? (
                        // Image upload message with labels
                        <div className="message-content">
                          <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                              <img 
                                src={msg.imagePreview} 
                                alt="Uploaded" 
                                style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #ddd' }} 
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#333', marginBottom: '0.5rem' }}>
                                  {msg.isAnalyzing ? '🔍 Analyzing image...' : '✅ Image uploaded & analyzed'}
                                </div>
                                {msg.labels && msg.labels.length > 0 && (
                                  <>
                                    <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.35rem' }}>
                                      Detected labels:
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                                      {msg.labels.slice(0, 5).map((label, idx) => (
                                        <span 
                                          key={idx}
                                          onClick={() => handleLabelClick(label.description)}
                                          style={{ 
                                            padding: '0.25rem 0.5rem', 
                                            background: '#e3f2fd', 
                                            color: '#1976d2', 
                                            borderRadius: '12px', 
                                            fontSize: '0.75rem',
                                            cursor: 'pointer',
                                            border: '1px solid #90caf9',
                                            transition: 'all 0.2s',
                                            fontWeight: 'bold'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.background = '#1976d2';
                                            e.target.style.color = '#fff';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.background = '#e3f2fd';
                                            e.target.style.color = '#1976d2';
                                          }}
                                          title={`Confidence: ${label.confidence}% - Click to search`}
                                        >
                                          {label.description} ({label.confidence}%)
                                        </span>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : msg.content === "UNSPSC_RESULTS" && msg.unspscResults ? (
                        <>
                          <div className="message-content">
                            <UNSPSCResultsTable results={msg.unspscResults} />
                          </div>
                          {msg.processingTime && (
                            <div style={{ fontSize: '0.75rem', color: '#444444ff', marginTop: '0.5rem' }}>
                              ⏱️ Processing time: {msg.processingTime}s
                            </div>
                          )}
                        </>
                      ) : msg.content && (msg.content.trim().startsWith('{') || msg.content.trim().startsWith('[')) ? (
                        <>
                          <pre className="message-content" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f5f5f5', padding: 12, borderRadius: 6, fontSize: 14, fontFamily: 'monospace'}}>{msg.content}</pre>
                          {msg.processingTime && (
                            <div style={{ fontSize: '0.75rem', color: '#444444ff', marginTop: '0.5rem' }}>
                              ⏱️ Processing time: {msg.processingTime}s
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {msg.role === "bot" ? (
                            <div className="message-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content) }} />
                          ) : (
                            <div className="message-content">{msg.content}</div>
                          )}
                          {msg.processingTime && (
                            <div style={{ fontSize: '0.75rem', color: '#444444ff', marginTop: '0.5rem' }}>
                              ⏱️ Processing time: {msg.processingTime}s
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )
                )}
                {isLoading && (
                  <div className="message bot">
                    <div className="message-content neon-glow">
                      <TextShimmer className="text-base font-mono" duration={2}>
                        SoPhia is thinking. Give it sometime to process....
                      </TextShimmer>
                    </div>
                  </div>
                )}
              </div>
              <form onSubmit={handleSubmit} className="chat-form" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isAnalyzingImage}
                  className="upload-btn"
                  style={{ 
                    padding: '0.6rem', 
                    background: '#4a8534', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: isAnalyzingImage ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s'
                  }}
                  title="Upload image to search"
                >
                  <FontAwesomeIcon icon={faUpload} />
                </button>
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    type="text"
                    value={userInput}
                    onChange={handleInputChange}
                    placeholder="Enter product or service name to get UNSPSC code..."
                    required
                    style={{ width: '100%', paddingRight: '2.5rem' }}
                  />
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="chat-send-btn"
                    style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
        </div>
    </>
  );
};

const VerifyIdentityPage = () => (
  <div>
    <h2>Verify Identity</h2>
    <p>This is the identity verification page</p>
  </div>
);

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={
          <>
            <Chatbot />
          </>
        } />
        <Route path="/verify-identity" element={<VerifyIdentityPage />} />
      </Routes>
    </Router>
  );
};

export default App;
